import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { call } from '../api/library.js';

// 상단 시세 흐름판 — 지수·환율·경제 뉴스 제목이 왼쪽으로 흘러감(마우스를 올리면 멈춤). 데이터는 서버가 1분마다 갱신
interface Quote { label: string; price: number; changePct: number | null; kind: 'index' | 'fx' }
interface Market { quotes: Quote[]; news: Array<{ title: string; link: string }>; at: string }

const fetchMarket = async () => (await (await call('/market')).json()) as Market;
const num = (v: number, kind: Quote['kind']) => v.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: kind === 'index' || v >= 1000 });
// 한국 관례: 상승 빨강, 하락 파랑
const tone = (p: number | null) => (p == null || p === 0 ? '#6B7280' : p > 0 ? '#DC2626' : '#2563EB');
const arrow = (p: number | null) => (p == null || p === 0 ? '' : p > 0 ? '▲' : '▼');

export function MarketTicker(): JSX.Element | null {
  const q = useQuery({ queryKey: ['market'], queryFn: fetchMarket, refetchInterval: 60_000, staleTime: 50_000 });
  const d = q.data;
  if (!d || (!d.quotes.length && !d.news.length)) return null;

  const items = (
    <>
      {d.quotes.map((x) => (
        <span key={x.label} className="dx-tick-item">
          <b style={{ color: '#374151' }}>{x.label}</b>
          <span className="tnum">{num(x.price, x.kind)}</span>
          {x.changePct != null && <span className="tnum" style={{ color: tone(x.changePct) }}>{arrow(x.changePct)}{Math.abs(x.changePct).toFixed(2)}%</span>}
        </span>
      ))}
      {d.news.map((n) => (
        <a key={n.link} className="dx-tick-item dx-tick-news" href={n.link} target="_blank" rel="noopener noreferrer">
          <span style={{ color: '#9CA3AF' }}>뉴스</span>{n.title}
        </a>
      ))}
    </>
  );
  // 같은 내용을 두 번 이어 붙여 -50% 이동 → 끊김 없이 반복. 속도는 길이에 비례
  const seconds = Math.max(40, (d.quotes.length * 14 + d.news.reduce((s, n) => s + n.title.length, 0)) * 0.25);
  return (
    <div className="dx-tick" title={`시세·뉴스 ${new Date(d.at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} 기준 · 지수: Yahoo Finance · 뉴스: 연합뉴스`}>
      <div className="dx-tick-track" style={{ animationDuration: `${seconds}s` }}>
        <div className="dx-tick-run">{items}</div>
        <div className="dx-tick-run" aria-hidden>{items}</div>
      </div>
    </div>
  );
}
